import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "../setupAxios";
import { useDialog } from "../context/dialogContext";
import FiltrosAlumnos from "./FiltrosAlumnos";
import { estadoCita, fechaES, fechaHoraES, msgError, primerNombre, useListado } from "../utils";

// {nombre} (da igual mayúsculas/minúsculas) se cambia por el primer nombre del alumno.
const rellenar = (texto, alumno) => texto.replace(/\{nombre\}/gi, primerNombre(alumno.nombre));
const urlWA = (telefono, texto) =>
  `https://wa.me/${(telefono || "").replace(/\D/g, "")}${texto ? `?text=${encodeURIComponent(texto)}` : ""}`;

// Botón de WhatsApp: sin mensajes activos abre el chat vacío; con uno va directo;
// con varios despliega sus títulos para elegir.
function BotonWhatsApp({ telefono, etiqueta = "WhatsApp", alumno, mensajes, alAbrir }) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!abierto) return undefined;
    const cerrar = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", cerrar);
    return () => document.removeEventListener("mousedown", cerrar);
  }, [abierto]);

  const abrir = (m) => {
    window.open(urlWA(telefono, m ? rellenar(m.texto, alumno) : ""), "_blank", "noopener");
    alAbrir(m);
    setAbierto(false);
  };
  if (!telefono) return <button className="btn btn-mini btn-wa" disabled title="Este alumno no tiene teléfono">Sin teléfono</button>;
  const onClick = () => (mensajes.length > 1 ? setAbierto((v) => !v) : abrir(mensajes[0] || null));
  return (
    <span className="ci-menu" ref={ref}>
      <button className="btn btn-mini btn-wa" onClick={onClick}>💬 {etiqueta}{mensajes.length > 1 ? " ▾" : ""}</button>
      {abierto && (
        <div className="ci-menu-lista">
          {mensajes.map((m) => (
            <button key={m.id} onClick={() => abrir(m)}>{m.titulo}<small>{rellenar(m.texto, alumno).slice(0, 70)}</small></button>
          ))}
        </div>
      )}
    </span>
  );
}

// Ventana "Mis mensajes": crear, activar/desactivar, editar y borrar.
function MisMensajes({ cerrar, alCambiar }) {
  const { confirmarPeligro, avisar } = useDialog();
  const [lista, setLista] = useState([]);
  const [edit, setEdit] = useState({ id: null, titulo: "", texto: "", activo: true });
  const cargar = useCallback(async () => {
    const { data } = await api.get("/admisiones/mensajes");
    setLista(data.mensajes);
    alCambiar();
  }, [alCambiar]);
  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    try {
      const cuerpo = { titulo: edit.titulo, texto: edit.texto, activo: edit.activo };
      if (edit.id) await api.put(`/admisiones/mensajes/${edit.id}`, cuerpo);
      else await api.post("/admisiones/mensajes", cuerpo);
      setEdit({ id: null, titulo: "", texto: "", activo: true });
      cargar();
    } catch (e) { avisar(msgError(e)); }
  };
  const alternar = async (m) => {
    try { await api.put(`/admisiones/mensajes/${m.id}`, { titulo: m.titulo, texto: m.texto, activo: !m.activo }); cargar(); }
    catch (e) { avisar(msgError(e)); }
  };
  const borrar = async (m) => {
    if (!(await confirmarPeligro(`¿Borrar el mensaje «${m.titulo}»?`, { titulo: "Borrar mensaje", boton: "Borrar" }))) return;
    try { await api.delete(`/admisiones/mensajes/${m.id}`); cargar(); } catch (e) { avisar(msgError(e)); }
  };

  return (
    <div className="modal-back" onMouseDown={cerrar}>
      <div className="modal ci-modal-ancho" onMouseDown={(e) => e.stopPropagation()}>
        <h3>💬 Mis mensajes</h3>
        <p className="muted">Escribe <b>{"{nombre}"}</b> donde quieras que salga el nombre del alumno. Solo los <b>activos</b> salen al pulsar WhatsApp.</p>
        <div className="tablewrap" style={{ marginBottom: 14 }}>
          <table className="t-mensajes">
            <thead><tr><th>Título</th><th>Texto</th><th>Estado</th><th /></tr></thead>
            <tbody>
              {lista.length === 0 && <tr><td colSpan={4} className="empty">Aún no tienes mensajes.</td></tr>}
              {lista.map((m) => (
                <tr key={m.id}>
                  <td className="strong">{m.titulo}</td>
                  <td className="ci-wrap">{m.texto}</td>
                  <td><button className={`btn btn-mini ${m.activo ? "btn-success" : ""}`} onClick={() => alternar(m)}>{m.activo ? "Activo" : "Inactivo"}</button></td>
                  <td><div className="acc-cell">
                    <button className="btn btn-mini" onClick={() => setEdit({ id: m.id, titulo: m.titulo, texto: m.texto, activo: !!m.activo })}>✏️</button>
                    <button className="btn btn-mini btn-danger" onClick={() => borrar(m)}>🗑</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <h4 style={{ margin: "0 0 8px" }}>{edit.id ? "Editar mensaje" : "Nuevo mensaje"}</h4>
        <div className="field"><label>Título (lo verás en el desplegable)</label>
          <input value={edit.titulo} maxLength={100} onChange={(e) => setEdit({ ...edit, titulo: e.target.value })} /></div>
        <div className="field"><label>Texto del mensaje</label>
          <textarea rows={4} value={edit.texto} onChange={(e) => setEdit({ ...edit, texto: e.target.value })}
                    placeholder="Hola {nombre}, ¿cuándo llegas a Pamplona?" /></div>
        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" style={{ width: "auto" }} checked={edit.activo} onChange={(e) => setEdit({ ...edit, activo: e.target.checked })} /> Activo
        </label>
        <div className="modal-actions">
          {edit.id && <button className="btn" onClick={() => setEdit({ id: null, titulo: "", texto: "", activo: true })}>Cancelar edición</button>}
          <button className="btn" onClick={cerrar}>Cerrar</button>
          <button className="btn btn-primary" onClick={guardar} disabled={!edit.titulo.trim() || !edit.texto.trim()}>
            {edit.id ? "Guardar cambios" : "Añadir mensaje"}
          </button>
        </div>
      </div>
    </div>
  );
}

function HistorialContactos({ alumno, cerrar }) {
  const [lista, setLista] = useState(null);
  useEffect(() => {
    api.get(`/admisiones/contactos/${alumno.id_user}`).then(({ data }) => setLista(data.contactos)).catch(() => setLista([]));
  }, [alumno.id_user]);
  return (
    <div className="modal-back" onMouseDown={cerrar}>
      <div className="modal modal-sm" onMouseDown={(e) => e.stopPropagation()}>
        <h3>🕘 Mensajes a {alumno.nombre}</h3>
        {lista === null ? <p className="muted">Cargando…</p> : lista.length === 0 ? <p className="muted">Todavía no se le ha escrito.</p> : (
          <ul style={{ paddingLeft: 18 }}>
            {lista.map((c, i) => <li key={i}>{fechaHoraES(c.fecha_hora)} · {c.por}{c.titulo ? ` · «${c.titulo}»` : ""}</li>)}
          </ul>
        )}
        <div className="modal-actions"><button className="btn btn-primary" onClick={cerrar}>Cerrar</button></div>
      </div>
    </div>
  );
}

// Área de admisiones: ver quién tiene fecha y quién no, escribir por WhatsApp y marcar "escrito hoy".
export default function PanelAdmisiones() {
  const { avisar, exito } = useDialog();
  const [f, setF] = useState({});
  const [opciones, setOpciones] = useState({});
  const [mensajes, setMensajes] = useState([]);
  const [verMensajes, setVerMensajes] = useState(false);
  const [historial, setHistorial] = useState(null);
  const ultimoMsg = useRef({});           // mensaje usado por última vez con cada alumno (para guardarlo al marcar)
  const { datos, cargando, error, recargar } = useListado("/admisiones/alumnos", f);

  const cargarMensajes = useCallback(() => {
    api.get("/admisiones/mensajes").then(({ data }) => setMensajes(data.mensajes)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/admisiones/opciones").then(({ data }) => setOpciones(data)).catch(() => {});
    cargarMensajes();
  }, [cargarMensajes]);

  const activos = mensajes.filter((m) => m.activo);
  const sinFecha = datos.filter((a) => a.estado === "sin_fecha").length;
  const conFecha = datos.filter((a) => a.estado === "con_cita").length;

  const marcar = async (a) => {
    try {
      if (a.escrito_hoy_por_mi) await api.delete(`/admisiones/contacto/${a.id_user}`);
      else { await api.post("/admisiones/contacto", { id_alumno: a.id_user, id_mensaje: ultimoMsg.current[a.id_user] || null }); exito(`Marcado: hoy se ha escrito a ${primerNombre(a.nombre)}`); }
      recargar();
    } catch (e) { avisar(msgError(e)); }
  };

  return (
    <div>
      <div className="card">
        <div className="card-headrow">
          <h3>Alumnos <span className="count">{datos.length}</span></h3>
          <button className="btn btn-primary" onClick={() => setVerMensajes(true)}>💬 Mis mensajes{activos.length ? ` (${activos.length})` : ""}</button>
        </div>
        <div className="ci-resumen">
          <div className="ci-chip verde"><b>{conFecha}</b><span>con fecha</span></div>
          <div className="ci-chip rojo"><b>{sinFecha}</b><span>sin nada</span></div>
        </div>
        <FiltrosAlumnos f={f} setF={setF} opciones={opciones} campos={["q", "piso", "estado", "fecha", "servicio", "a_r"]} />
        {error && <div className="alert alert-error">{error}</div>}
        <div className="tablewrap">
          <table className="ci-tabla t-admisiones">
            <thead><tr><th>Alumno</th><th>Piso / hab.</th><th>Check-in</th><th>Teléfono</th><th>WhatsApp</th><th>Escrito hoy</th><th /></tr></thead>
            <tbody>
              {cargando && datos.length === 0 && <tr><td colSpan={7} className="empty">Cargando…</td></tr>}
              {!cargando && datos.length === 0 && <tr><td colSpan={7} className="empty">No hay alumnos con esos filtros.</td></tr>}
              {datos.map((a) => {
                const est = estadoCita(a);
                return (
                  <tr key={a.id_user}>
                    <td className="ci-nombre" title={a.email}>{a.nombre}</td>
                    <td title={a.pisos_texto}>{a.pisos_texto || <span className="muted">—</span>}</td>
                    <td><span className={`badge ${est.clase}`}>{est.texto}</span>{a.llegado ? <span className="badge badge-blue" style={{ marginLeft: 6 }}>Llegado</span> : null}</td>
                    <td className="mono">{a.telefono_wa || <span className="muted">—</span>}
                      {a.telefono_wa && <small className="muted"> ({a.telefono_origen})</small>}</td>
                    <td className="ci-accion"><div className="acc-cell">
                      <BotonWhatsApp telefono={a.telefono_wa} alumno={a} mensajes={activos}
                                     alAbrir={(m) => { ultimoMsg.current[a.id_user] = m ? m.id : null; }} />
                      {a.telefono_alt && (
                        <BotonWhatsApp telefono={a.telefono_alt} etiqueta="Otro nº" alumno={a} mensajes={activos}
                                       alAbrir={(m) => { ultimoMsg.current[a.id_user] = m ? m.id : null; }} />
                      )}
                    </div></td>
                    <td>
                      <button className={`btn btn-mini btn-ok ${a.escrito_hoy_por_mi ? "on" : "pend"}`} onClick={() => marcar(a)}
                              title={a.contactos_hoy.length ? a.contactos_hoy.map((c) => `${c.por}${c.titulo ? ` («${c.titulo}»)` : ""}`).join(", ") : "Marcar que se le ha escrito hoy"}>
                        {a.escrito_hoy_por_mi ? "✓ Escrito hoy" : a.contactos_hoy.length ? "✓ Otro lo hizo" : "Marcar"}
                      </button>
                    </td>
                    <td><button className="btn btn-mini" onClick={() => setHistorial(a)} title="Historial de mensajes">🕘{a.ultimo_contacto ? ` ${fechaES(a.ultimo_contacto).slice(0, 5)}` : ""}</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {verMensajes && <MisMensajes cerrar={() => setVerMensajes(false)} alCambiar={cargarMensajes} />}
      {historial && <HistorialContactos alumno={historial} cerrar={() => { setHistorial(null); recargar(); }} />}
    </div>
  );
}
